import { useState, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Shield, Eye, EyeOff } from 'lucide-react'
import { MeshBackground } from '../components/MeshBackground'
import { GlassPanel, GlassButton, GlassInput, GlassBadge } from '../components/ui/glass'
import { useAuth } from './useAuth'

export function LoginPage() {
  const { login, loginWithWallet, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [walletLoading, setWalletLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    clearError()
    try {
      await login(email, password)
      navigate('/dashboard')
    } catch {}
  }

  const handleWalletConnect = async () => {
    clearError()
    setWalletLoading(true)
    try {
      await loginWithWallet()
      navigate('/dashboard')
    } catch {} finally {
      setWalletLoading(false)
    }
  }

  return (
    <div className="min-h-screen font-body text-white flex items-center justify-center p-4">
      <MeshBackground />
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-brand flex items-center justify-center shadow-glow-sky mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display text-display-sm text-white">Welcome back</h1>
          <p className="text-white/50 text-sm mt-1">Sign in to MEV Shield</p>
        </div>

        <GlassPanel gradient padding="lg">
          {error && (
            <div className="mb-4">
              <GlassBadge variant="error">{error}</GlassBadge>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <GlassInput
              type="email"
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <div className="relative">
              <GlassInput
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[34px] text-white/40 hover:text-white/70 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex justify-end">
              <button type="button" className="text-sm text-primary/80 hover:text-primary transition-colors">
                Forgot password?
              </button>
            </div>

            <GlassButton type="submit" size="full" loading={isLoading}>
              Sign In →
            </GlassButton>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <GlassButton variant="secondary" size="full" onClick={handleWalletConnect} loading={walletLoading}>
            🦊 Connect MetaMask
          </GlassButton>

          <p className="text-center text-sm text-white/40 mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary-light transition-colors">
              Sign up →
            </Link>
          </p>
        </GlassPanel>
      </div>
    </div>
  )
}
